<a class="skip" href="#contenido">Saltar al contenido</a>

<header class="top">
  <div class="top__inner">
    <a class="mark" routerLink="/" aria-label="Aurea Licita, inicio">
      <lc-logo class="mark__logo" variant="mark" [decorative]="true" />
    </a>

    <nav class="nav" aria-label="Secciones">
      <a href="#problema">El problema</a>
      <a href="#producto">Qué hace</a>
      <a href="#pasos">Cómo funciona</a>
      <a href="#planes">Planes</a>
    </nav>

    <div class="top__actions">
      <lc-theme-toggle />
      <a class="btn btn--ghost" routerLink="/login">Iniciar sesión</a>
      <a class="btn btn--solid" routerLink="/login">Crear cuenta</a>
    </div>
  </div>
</header>

<main id="contenido">
  <!-- ── Héroe ──────────────────────────────────────────────────────────── -->
  <section class="hero">
    <div class="hero__text">
      <p class="eyebrow">Licitaciones públicas · Latinoamérica</p>
      <h1 class="hero__title">No pierdas una licitación por un papel.</h1>
      <p class="hero__lead">
        Aurea Licita vigila los procesos que te importan, te avisa cuando el pliego cambia y revisa tu
        documentación antes de que la envíes.
      </p>
      <div class="hero__cta">
        <a class="btn btn--solid btn--lg" routerLink="/login">Crear cuenta gratis</a>
        <a class="btn btn--quiet btn--lg" href="#pasos">Ver cómo funciona</a>
      </div>
      <p class="hero__foot">
        Sin tarjeta. Entras con la cuenta de Microsoft o de Google que ya usas.
      </p>
    </div>

    <!-- El envoltorio existe para el desplazamiento con el scroll; la hoja
         se reserva su propia entrada. Ver styles/_motion.scss. -->
    <div class="hero__aside">
      <figure class="sheet">
        <div class="sheet__head">
          <span class="sheet__source">SECOP II · Colombia</span>
          <span class="sheet__folio">Folio 04</span>
        </div>

        <p class="sheet__title">
          Operación logística de eventos institucionales para la vigencia 2026
        </p>

        <dl class="sheet__meta">
          <div>
            <dt>Entidad</dt>
            <dd>Ministerio de Comercio</dd>
          </div>
          <div>
            <dt>Modalidad</dt>
            <dd>Licitación pública</dd>
          </div>
        </dl>

        <div class="diff" lcReveal [lcRevealDelay]="420">
          <p class="diff__label">Adenda 2 · detectada hace 4 minutos</p>
          <p class="diff__old"><s>Cierre: jueves 12 de marzo, 15:00</s></p>
          <p class="diff__new">Cierre: jueves 19 de marzo, 15:00</p>
          <p class="diff__note">Cambió respecto de la versión que descargaste el 2 de marzo.</p>
        </div>

        <figcaption class="stamp" lcReveal [lcRevealDelay]="1200">
          <span class="stamp__verdict">Falta 1 documento</span>
          <span class="stamp__reason">Certificado ISO 27001 vencido el 28 de febrero</span>
        </figcaption>
      </figure>
    </div>
  </section>

  <!-- ── Problema ───────────────────────────────────────────────────────── -->
  <section id="problema" class="band">
    <h2 class="band__title">Las ofertas no se caen donde uno cree.</h2>
    <ol class="issues">
      <li class="issue" lcReveal>
        <span class="issue__num">01</span>
        <h3>La adenda que nadie vio</h3>
        <p>
          Un pliego se modifica después de publicado. Si el cambio no llega a tiempo, semanas de
          trabajo se presentan contra una versión que ya no existe.
        </p>
      </li>
      <li class="issue" lcReveal [lcRevealDelay]="120">
        <span class="issue__num">02</span>
        <h3>El certificado vencido</h3>
        <p>
          Un documento que expiró la semana pasada no avisa. Aparece al armar la carpeta, con el
          reloj encima, o no aparece hasta que la oferta ya se envió.
        </p>
      </li>
      <li class="issue" lcReveal [lcRevealDelay]="240">
        <span class="issue__num">03</span>
        <h3>El rechazo por forma</h3>
        <p>
          Se puede tener el mejor precio y la mejor propuesta técnica y quedar fuera por un
          requisito de forma que se pasó por alto.
        </p>
      </li>
    </ol>
  </section>

  <!-- ── Producto ───────────────────────────────────────────────────────── -->
  <section id="producto" class="band band--tight">
    <h2 class="band__title">Tres piezas, un mismo trabajo.</h2>

    <!-- Los pilares alternan el lado de entrada para que la lectura avance en
         zigzag y no como una columna que empuja siempre desde abajo. -->
    <div class="pillar" lcReveal data-reveal="left">
      <div class="pillar__body">
        <p class="eyebrow">Radar</p>
        <h3>Encuentra sin abrir seis portales</h3>
        <p>
          Una sola búsqueda sobre los portales de contratación de la región. Filtra por entidad,
          objeto, modalidad y cuantía. Guarda el perfil de búsqueda y deja que él te busque a ti.
        </p>
      </div>
      <ul class="portals">
        <li lcReveal><span>Colombia</span> SECOP II</li>
        <li lcReveal [lcRevealDelay]="70"><span>Chile</span> Mercado Público</li>
        <li lcReveal [lcRevealDelay]="140"><span>Perú</span> SEACE</li>
        <li lcReveal [lcRevealDelay]="210"><span>Ecuador</span> SERCOP</li>
        <li lcReveal [lcRevealDelay]="280"><span>Paraguay</span> DNCP</li>
        <li lcReveal [lcRevealDelay]="350"><span>R. Dominicana</span> Portal Transaccional</li>
      </ul>
    </div>

    <div class="pillar pillar--flip" lcReveal data-reveal="right">
      <div class="pillar__body">
        <p class="eyebrow">Expediente</p>
        <h3>Sabe qué te deja fuera antes de enviar</h3>
        <p>
          El checklist vivo del proceso: qué aplica, qué cumples, qué falta. Al final no hay un
          puntaje, hay una lista de motivos con el indicador, tu valor y lo que exige el pliego.
        </p>
      </div>
      <ul class="reasons">
        <li class="reasons__row reasons__row--block" lcReveal>
          <span>Índice de liquidez</span><span>1,14 frente a ≥ 1,70 exigido</span>
        </li>
        <li class="reasons__row reasons__row--block" lcReveal [lcRevealDelay]="90">
          <span>Nivel de endeudamiento</span><span>75% frente a ≤ 60% exigido</span>
        </li>
        <li class="reasons__row reasons__row--ok" lcReveal [lcRevealDelay]="180">
          <span>Experiencia acreditada</span><span>Cumple</span>
        </li>
      </ul>
    </div>

    <div class="pillar" lcReveal data-reveal="left">
      <div class="pillar__body">
        <p class="eyebrow">Vigilancia</p>
        <h3>Trabaja con la app cerrada</h3>
        <p>
          Aurea Licita revisa los procesos que sigues por su cuenta. Cuando aparece una adenda te muestra
          qué cambió respecto de la versión que descargaste tú, no respecto de la anterior del
          sistema.
        </p>
      </div>
      <ul class="timeline">
        <li lcReveal><span>02 mar</span> Descargaste el pliego</li>
        <li lcReveal [lcRevealDelay]="90"><span>08 mar</span> Adenda 1 · dos anexos nuevos</li>
        <li class="is-live" lcReveal [lcRevealDelay]="180">
          <span>11 mar</span> Adenda 2 · el cierre se movió
        </li>
      </ul>
    </div>
  </section>

  <!-- ── Pasos ──────────────────────────────────────────────────────────── -->
  <section id="pasos" class="band">
    <h2 class="band__title">Cuatro pasos y ya está corriendo.</h2>
    <ol class="steps">
      <li lcReveal>
        <h3>Cargas la empresa una vez</h3>
        <p>
          Cámara de comercio, RUT, certificados, indicadores financieros y los datos del
          representante legal.
        </p>
      </li>
      <li lcReveal [lcRevealDelay]="100">
        <h3>Defines qué te interesa</h3>
        <p>País, objeto, cuantía. El radar trae los procesos nuevos que encajan con ese perfil.</p>
      </li>
      <li lcReveal [lcRevealDelay]="200">
        <h3>Evalúas antes de invertir horas</h3>
        <p>
          Cada proceso llega con veredicto: cumples, no cumples o falta información. Siempre con el
          motivo.
        </p>
      </li>
      <li lcReveal [lcRevealDelay]="300">
        <h3>Presentas con la última versión</h3>
        <p>
          Aurea Licita vigila hasta el cierre y te avisa de cada cambio por correo, por Teams o dentro de
          la app.
        </p>
      </li>
    </ol>
  </section>

  <!-- ── Planes ─────────────────────────────────────────────────────────── -->
  <section id="planes" class="band band--tight">
    <h2 class="band__title">Se paga por lo que usas, no por cuántos son.</h2>
    <p class="band__lead">
      Los usuarios son ilimitados desde el primer plan de pago. Cobrar por asiento es cobrarle a un
      equipo por colaborar, y una licitación nunca la arma una sola persona.
    </p>

    <div class="plans">
      <table class="plans__table">
        <caption class="sr-only">
          Comparación de funciones por plan
        </caption>
        <thead>
          <tr>
            <th scope="col">Función</th>
            <th scope="col">Gratis</th>
            <th scope="col">Data</th>
            <th scope="col">Pro</th>
            <th scope="col">Equipo</th>
          </tr>
        </thead>
        <tbody>
          @for (row of planRows; track row.label) {
            <tr lcReveal [lcRevealDelay]="$index * 60">
              <th scope="row">{{ row.label }}</th>
              @for (cell of row.cells; track $index) {
                <td [class.is-off]="cell === '—'">{{ cell }}</td>
              }
            </tr>
          }
        </tbody>
      </table>
    </div>
    <p class="plans__note">
      La lectura del pliego con IA depende de que el portal del país publique los documentos. Donde
      no los publica, el resto del producto funciona igual.
    </p>
  </section>

  <!-- ── Cierre ─────────────────────────────────────────────────────────── -->
  <section class="close" lcReveal data-reveal="scale">
    <h2>Empieza por el proceso que tienes abierto ahora.</h2>
    <p>Cárgalo, deja que Aurea Licita lo vigile y mira qué te falta antes de la fecha de cierre.</p>
    <a class="btn btn--solid btn--lg" routerLink="/login">Crear cuenta gratis</a>
  </section>
</main>

<footer class="foot">
  <lc-logo class="foot__mark" />
  <p class="foot__legal">Un producto de Opi Technology · {{ year }}</p>
</footer>
